import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slider,
  Typography,
} from "@mui/material";
import API from "../../services/API";
import { useContent } from "../../context/ContentContext";
import ElementOptimizationFields from "../elementUploads/ElementOptimizationFields";
import NewElementUploader from "../elementUploads/NewElementUploader";
import ElementCloneIndicator from "../elements/ElementCloneIndicator";
import useElementCloneStatuses from "../elements/useElementCloneStatuses";
import WorldUnitEditor from "../WorldUnitEditor";
import { AppButton, AppTextField, Panel } from "../ui";
import {
  buildElementOptimizationPayload,
  createElementOptimizationFormFromPayload,
  isValidElementOptimizationForm,
} from "../../utility/elementOptimization";

const CollectionElementsSection = ({ elements }) => {
  const { trackElementBuild } = useContent();
  const [elementsState, setElementsState] = useState(elements || []);
  const [editingId, setEditingId] = useState(null);
  const [editedElements, setEditedElements] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [elementToDelete, setElementToDelete] = useState(null);
  const cloneStatuses = useElementCloneStatuses(elementsState);

  useEffect(() => {
    setElementsState(elements || []);
  }, [elements]);

  const handleEdit = async (elementId) => {
    try {
      await API.get(`elements/${elementId}/status`, {
        requiresAuth: true,
      });
      const response = await API.get(`elements/${elementId}`, {
        requiresAuth: true,
      });
      const fullElement = response?.data?.data || response?.data;

      setEditingId(elementId);
      setEditedElements((prev) => ({
        ...prev,
        [elementId]: {
          displayName: fullElement.labelData.displayName,
          description: fullElement.labelData.description,
          worldUnit: fullElement.worldUnit,
          resolution: fullElement.visualizerData.resolution,
          optimization: createElementOptimizationFormFromPayload(
            fullElement.optimization
          ),
          fullElement: fullElement,
        },
      }));
    } catch (error) {
      console.error("Error fetching full element data:", error);
    }
  };

  const handleChange = (elementId, field, value) => {
    setEditedElements((prev) => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        [field]: value,
      },
    }));
  };

  const handleWorldUnitChange = (elementId, field, value) => {
    setEditedElements((prev) => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        worldUnit: {
          ...prev[elementId].worldUnit,
          [field]: value,
        },
      },
    }));
  };

  const handleOptimizationChange = (elementId, optimization) => {
    setEditedElements((prev) => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        optimization,
      },
    }));
  };

  const clearEditingState = (elementId) => {
    setEditingId((currentEditingId) =>
      currentEditingId === elementId ? null : currentEditingId
    );
    setEditedElements((prev) => {
      if (!elementId || !prev[elementId]) {
        return prev;
      }

      const nextEditedElements = { ...prev };
      delete nextEditedElements[elementId];
      return nextEditedElements;
    });
  };

  const handleCancel = () => {
    clearEditingState(editingId);
  };

  const handleSave = async (elementId) => {
    const currentData = editedElements[elementId];
    if (!currentData || !currentData.fullElement) return;
    const updatedElement = {
      ...currentData.fullElement,
      labelData: {
        ...currentData.fullElement.labelData,
        displayName: currentData.displayName,
        description: currentData.description,
      },
      worldUnit: currentData.worldUnit,
      optimization: buildElementOptimizationPayload(currentData.optimization),
      visualizerData: {
        ...currentData.fullElement.visualizerData,
        resolution: currentData.resolution,
      },
    };

    try {
      await API.post("elements/", updatedElement, { requiresAuth: true });
      setElementsState((prev) =>
        prev.map((el) =>
          el.id === elementId
            ? {
                ...el,
                name: updatedElement.labelData.displayName,
                description: updatedElement.labelData.description,
              }
            : el
        )
      );
      clearEditingState(elementId);
    } catch (error) {
      console.error("Error saving element:", error);
    }
  };

  const retriggerBuild = async (elementId) => {
    const currentData = editedElements[elementId];
    const element = currentData?.fullElement;
    if (!element?.id) return;

    try {
      await API.post(
        "build-element",
        {
          ...element,
          optimization: buildElementOptimizationPayload(currentData.optimization),
        },
        {
          requiresAuth: true,
        }
      );
      clearEditingState(element.id);
      trackElementBuild(element.id);
    } catch (error) {
      console.error("Error rebuilding element:", error);
    }
  };

  const handleDeleteClick = (element) => {
    setElementToDelete(element);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!elementToDelete) return;
    try {
      await API.get(`elements/${elementToDelete.id}/delete`, {
        requiresAuth: true,
      });
      setElementsState((prev) => prev.filter((el) => el.id !== elementToDelete.id));
    } catch (error) {
      console.error("Error deleting element:", error);
    } finally {
      setDeleteConfirmOpen(false);
      setElementToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setElementToDelete(null);
  };

  const getStatusClass = (status) => {
    if (status?.includes("COMPLETE")) return "complete";
    if (status?.includes("FAILED")) return "failed";
    return "pending";
  };

  return (
    <section className="collectionDetailSection">
      <Typography className="collectionDetailSectionTitle" variant="h5">
        Elements ({elementsState.length})
      </Typography>

      <div className="elementsList app-row-list">
        {elementsState.map((element) => (
          <Panel key={element.id} className="elementCard" hoverable>
            {editingId === element.id ? (
              <div className="elementEditForm">
                <div className="elementEditFields">
                  <AppTextField
                    label="Display Name"
                    value={editedElements[element.id]?.displayName || ""}
                    onChange={(e) => handleChange(element.id, "displayName", e.target.value)}
                    fullWidth
                  />
                  <AppTextField
                    label="Description"
                    value={editedElements[element.id]?.description || ""}
                    onChange={(e) => handleChange(element.id, "description", e.target.value)}
                    fullWidth
                  />
                </div>

                <div className="elementEditActions">
                  <AppButton onClick={() => handleSave(element.id)}>
                    Save
                  </AppButton>
                  <AppButton kind="ghost" onClick={handleCancel}>
                    Cancel
                  </AppButton>
                  <AppButton
                    kind="ghost"
                    onClick={() => retriggerBuild(element.id)}
                    disabled={
                      !isValidElementOptimizationForm(
                        editedElements[element.id]?.optimization
                      )
                    }
                  >
                    Rebuild
                  </AppButton>
                </div>

                <div className="elementEditExtras">
                  <Box sx={{ width: 400 }}>
                    <WorldUnitEditor
                      worldUnit={editedElements[element.id]?.worldUnit}
                      onChange={(field, value) => handleWorldUnitChange(element.id, field, value)}
                    />
                  </Box>

                  <Box sx={{ width: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Resolution
                    </Typography>
                    <Slider
                      value={editedElements[element.id]?.resolution || 0.7}
                      onChange={(e, value) => handleChange(element.id, "resolution", value)}
                      min={0.7}
                      max={1}
                      step={0.005}
                      valueLabelDisplay="auto"
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 320px", maxWidth: 520 }}>
                    <ElementOptimizationFields
                      optimization={editedElements[element.id]?.optimization}
                      onChange={(optimization) =>
                        handleOptimizationChange(element.id, optimization)
                      }
                    />
                  </Box>
                </div>

                <Box sx={{ mt: 2 }}>
                  <NewElementUploader
                    initialElementId={element.id}
                    initialElementData={editedElements[element.id]?.fullElement}
                  />
                </Box>
              </div>
            ) : (
              <div className="elementCardContent">
                <div className="elementCardInfo">
                  <div className="elementCardNameRow">
                    <div className="elementCardName">{element.name || "No Name"}</div>
                    <ElementCloneIndicator isClone={cloneStatuses[element.id]} />
                  </div>
                  <div className="elementCardId">{element.id || "No ID"}</div>
                  <div className={`elementCardStatus ${getStatusClass(element.status?.type)}`}>
                    {element.status?.type} | {element.status?.timestamp}
                  </div>
                </div>

                <div className="elementCardDesc">
                  {element.description || "No Description"}
                </div>

                <div className="elementCardActions">
                  <AppButton
                    className="elementCardBtn"
                    kind="secondary"
                    onClick={() => handleEdit(element.id)}
                  >
                    Edit
                  </AppButton>
                  <AppButton
                    className="elementCardBtn"
                    kind="danger"
                    onClick={() => handleDeleteClick(element)}
                  >
                    Delete
                  </AppButton>
                </div>
              </div>
            )}
          </Panel>
        ))}
      </div>

      <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            {elementToDelete ? elementToDelete.name || "this element" : "this element"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton kind="ghost" onClick={cancelDelete}>
            Cancel
          </AppButton>
          <AppButton kind="danger" onClick={confirmDelete}>
            Delete
          </AppButton>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default CollectionElementsSection;
